import React, { useEffect, useState } from "react";
import Content from "../../Components/Core/Content";
import Loader from "../../Components/Core/Loader";
import Notification from "../../Components/Core/Notification";
import PersianDatePicker from "../../Components/Core/DatePicker";
import { getAuditLogs, getAuditCategories } from "../../Services/AuditService";
import {
  Button,
  Chip,
  FormControl,
  MenuItem,
  Pagination,
  Select,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import FormInput from "../../Components/Core/FormInput";

const levelLabels = {
  Information: "اطلاعات",
  Warning: "هشدار",
  Critical: "بحرانی",
};

const levelColors = {
  Information: "info",
  Warning: "warning",
  Critical: "error",
};

const categoryLabels = {
  Auth: "احراز هویت",
  Blog: "مقالات",
  Comment: "نظرات",
  Reservation: "رزرو",
  Consultation: "مشاوره",
  Payment: "پرداخت",
  Subscription: "اشتراک",
  Settings: "تنظیمات",
  SMS: "پیامک",
  System: "سیستم",
};

const iranSansSx = {
  fontFamily: "iran-sans, MyPersianFont, sans-serif",
  "& .MuiSelect-select": {
    fontFamily: "iran-sans, MyPersianFont, sans-serif",
  },
  "& .MuiMenuItem-root": {
    fontFamily: "iran-sans, MyPersianFont, sans-serif",
  },
  "& .MuiChip-label": {
    fontFamily: "iran-sans, MyPersianFont, sans-serif",
  },
  "& .MuiPaginationItem-root": {
    fontFamily: "iran-sans, MyPersianFont, sans-serif",
  },
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const [fromDate, setFromDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1)),
  );
  const [toDate, setToDate] = useState(new Date());
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [level, setLevel] = useState("");

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true);
      const fromDateTime = new Date(fromDate);
      fromDateTime.setHours(0, 0, 0, 0);
      const toDateTime = new Date(toDate);
      toDateTime.setHours(23, 59, 59, 999);

      const filter = {
        fromDate: fromDateTime.toISOString(),
        toDate: toDateTime.toISOString(),
        search: search.trim(),
        category: category || null,
        level: level || null,
        page,
        pageSize: itemsPerPage,
      };

      const response = await getAuditLogs(filter);
      setLogs(response.items || []);
      setTotalItems(response.totalCount || 0);
      setCurrentPage(page);
    } catch (error) {
      setSnackbarMessage("خطا در دریافت لاگ‌های سیستم");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setHasActiveFilters(search.trim() || category || level);
    fetchLogs(1);
  };

  const handleReset = () => {
    setSearch("");
    setCategory("");
    setLevel("");
    setFromDate(new Date(new Date().setMonth(new Date().getMonth() - 1)));
    setToDate(new Date());
    setHasActiveFilters(false);
    fetchLogs(1);
  };

  const handlePageChange = (event, value) => {
    fetchLogs(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    document.title = "لاگ سیستم";
    getAuditCategories()
      .then(setCategories)
      .catch(() => {});
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Content>
      <div className="container color-blue mb-5 iran-sans">
        <h2 className="mb-4 row mt-5 highlight-box highlight-secondary fw-bold color-blue iran-sans">
          <span>لاگ سیستم</span>
        </h2>

        <div className="row mt-4">
          <div className="col-12">
            <div className="card shadow-sm border-0 p-3">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label fw-bold iran-sans mb-1">
                    از تاریخ
                  </label>
                  <PersianDatePicker
                    value={fromDate}
                    onChange={(date) => setFromDate(date)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold iran-sans mb-1">
                    تا تاریخ
                  </label>
                  <PersianDatePicker
                    value={toDate}
                    onChange={(date) => setToDate(date)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold iran-sans mb-1">
                    دسته‌بندی
                  </label>
                  <FormControl fullWidth size="small">
                    <Select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      displayEmpty
                      sx={iranSansSx}
                      MenuProps={{
                        PaperProps: {
                          sx: iranSansSx,
                        },
                      }}
                    >
                      <MenuItem value="" sx={iranSansSx}>
                        همه
                      </MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat} value={cat} sx={iranSansSx}>
                          {categoryLabels[cat] || cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold iran-sans mb-1">
                    سطح
                  </label>
                  <FormControl fullWidth size="small">
                    <Select
                      value={level}
                      onChange={(e) => setLevel(e.target.value)}
                      displayEmpty
                      sx={iranSansSx}
                      MenuProps={{
                        PaperProps: {
                          sx: iranSansSx,
                        },
                      }}
                    >
                      <MenuItem value="" sx={iranSansSx}>
                        همه
                      </MenuItem>
                      <MenuItem value="Information" sx={iranSansSx}>
                        اطلاعات
                      </MenuItem>
                      <MenuItem value="Warning" sx={iranSansSx}>
                        هشدار
                      </MenuItem>
                      <MenuItem value="Critical" sx={iranSansSx}>
                        بحرانی
                      </MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormInput
                    label="جستجو"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="عملیات، جزئیات، نام کاربر..."
                    className="iran-sans"
                  />
                </div>
                <div className="col-md-6 d-flex gap-2">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    startIcon={<FontAwesomeIcon icon={faSearch} />}
                    sx={iranSansSx}
                    className="iran-sans"
                  ></Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<FontAwesomeIcon icon={faRotateRight} />}
                    sx={iranSansSx}
                    className="iran-sans ms-2"
                  ></Button>
                  {hasActiveFilters && (
                    <Chip
                      label="فیلتر فعال"
                      color="primary"
                      size="small"
                      sx={iranSansSx}
                      className="iran-sans"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="row mt-4">
              <div className="col-12">
                <div className="table-responsive">
                  <table className="table table-hover table-striped align-middle iran-sans">
                    <thead className="table-dark">
                      <tr>
                        <th>تاریخ</th>
                        <th>کاربر</th>
                        <th>دسته</th>
                        <th>عملیات</th>
                        <th>سطح</th>
                        <th>موجودیت</th>
                        <th>جزئیات</th>
                        <th>IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length === 0 ? (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center py-4 iran-sans"
                          >
                            موردی یافت نشد
                          </td>
                        </tr>
                      ) : (
                        logs.map((log) => (
                          <tr key={log.id}>
                            <td className="small iran-sans">
                              {log.createdAtDisplay}
                            </td>
                            <td className="iran-sans">
                              {log.userFullName || "—"}
                              {log.userMobileNumber && (
                                <div className="small text-muted iran-sans">
                                  {log.userMobileNumber}
                                </div>
                              )}
                            </td>
                            <td className="iran-sans">
                              {categoryLabels[log.category] || log.category}
                            </td>
                            <td className="small fw-semibold iran-sans">
                              {log.action}
                            </td>
                            <td>
                              <Chip
                                label={levelLabels[log.level] || log.level}
                                color={levelColors[log.level] || "default"}
                                size="small"
                                sx={iranSansSx}
                                className="iran-sans"
                              />
                            </td>
                            <td className="small iran-sans">
                              {log.entityType
                                ? `${log.entityType} #${log.entityId ?? ""}`
                                : "—"}
                            </td>
                            <td
                              className="small iran-sans"
                              style={{ maxWidth: 280 }}
                            >
                              {log.details || "—"}
                            </td>
                            <td className="small iran-sans">
                              {log.ipAddress || "—"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  sx={iranSansSx}
                />
              </div>
            )}

            <div className="text-center mt-2 text-muted small iran-sans">
              مجموع: {totalItems} رکورد
            </div>
          </>
        )}
      </div>

      <Notification
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setOpenSnackbar(false)}
      />
    </Content>
  );
};

export default AuditLogs;
